import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useLicense } from '@/hooks/useLicense'
import { ReactNode, useRef, useEffect, useState } from 'react'
import { authService } from '@/services/authService'
import ContractAgreementModal, { type ContractAgreementModalRef } from '@/components/modals/ContractAgreementModal'

interface ProtectedRouteProps {
  children: ReactNode
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, user, logout, updateUser } = useAuth()
  const { expired: licenseExpired } = useLicense()
  const location = useLocation()
  const contractModalRef = useRef<ContractAgreementModalRef>(null)
  const [hasCheckedContract, setHasCheckedContract] = useState(false)

  // Check if user needs to agree to contract
  useEffect(() => {
    if (isAuthenticated && user && !hasCheckedContract) {
      // Check if contract_agreement is null or undefined (not yet agreed)
      const needsToAgree = !user.contract_agreement

      if (needsToAgree) {
        contractModalRef.current?.open()
      }

      setHasCheckedContract(true)
    }
  }, [isAuthenticated, user, hasCheckedContract])

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // When the license is expired, confine every logged-in user (owner/admin are
  // the only ones who can authenticate at all) to the Plans page so they can
  // only renew the license. Any other route is redirected to Plans. The backend
  // enforces the actual lockdown; this is the matching UI behaviour.
  if (licenseExpired && !location.pathname.startsWith('/plans')) {
    return <Navigate to="/plans?reason=license_expired" replace />
  }

  const handleAgreeContract = async () => {
    // Call agreeContract which will automatically fetch updated profile
    const updatedUser = await authService.agreeContract()
    // Update user state in context
    updateUser(updatedUser)
  }

  const handleDeclineContract = () => {
    logout()
  }

  return (
    <>
      {children}
      <ContractAgreementModal
        ref={contractModalRef}
        onAgree={handleAgreeContract}
        onDecline={handleDeclineContract}
      />
    </>
  )
}
